import { useCallback, useState } from 'react';
import { ButtonDropDown, ButtonDropDownItem } from '@creditflowresearch/uilib/ButtonDropDown';
import {
  BondScreenerCreateViewModal,
} from '@/creditflowresearch/ui/common/components/molecules/BondScreenerCreateViewModal';
import { BOND_SCREENER_VIEWS_LIMIT_MESSAGE } from '@/creditflowresearch/application/BondScreener/Sidebar/bondScreenerViewsConstants';

interface IBondScreenerApplySaveButtonProps {
  dataTest: string,
  disabled?: boolean,
  canSaveActiveView: boolean,
  canCreateView?: boolean,
  existingNames: string[],
  applyAndSaveViewTooltip?: string,
  applyAndSaveNewViewTooltip?: string,
  onApplyOnly: () => void,
  onApplyAndSaveView: () => void,
  onApplyAndSaveAsNewView: (name: string) => void,
}

const BondScreenerApplySaveButton = ({
  dataTest,
  disabled = false,
  canSaveActiveView,
  canCreateView = false,
  existingNames,
  applyAndSaveViewTooltip,
  applyAndSaveNewViewTooltip,
  onApplyOnly,
  onApplyAndSaveView,
  onApplyAndSaveAsNewView,
}: IBondScreenerApplySaveButtonProps) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const toggleCreateModal = useCallback((isVisible: boolean): void => {
    setIsCreateModalOpen(isVisible);
  }, []);

  const handleCreateView = useCallback((name: string): void => {
    toggleCreateModal(false);
    onApplyAndSaveAsNewView(name);
  }, [toggleCreateModal, onApplyAndSaveAsNewView]);

  const label = canSaveActiveView ? 'Apply & Save' : 'Apply Only';
  const handleDefaultAction = canSaveActiveView ? onApplyAndSaveView : onApplyOnly;
  const actionDataTest = `${ dataTest }${ canSaveActiveView ? 'ApplyAndSave' : 'ApplyOnly' }`;

  return (
    <>
      <ButtonDropDown
        dataTest={ dataTest }
        actionDataTest={ actionDataTest }
        triggerDataTest={ dataTest }
        label={ label }
        disabled={ disabled }
        onClick={ handleDefaultAction }
      >
        <ButtonDropDownItem
          dataTest={ `${ dataTest }ApplyOnly` }
          onClick={ onApplyOnly }
        >
          Apply Only
        </ButtonDropDownItem>
        <ButtonDropDownItem
          dataTest={ `${ dataTest }ApplyAndSaveView` }
          onClick={ onApplyAndSaveView }
          disabled={ !canSaveActiveView }
          tooltipContent={ applyAndSaveViewTooltip ?? (!canSaveActiveView ? 'Select a view to update it' : undefined) }
        >
          Apply and Save View
        </ButtonDropDownItem>
        <ButtonDropDownItem
          dataTest={ `${ dataTest }ApplyAndSaveAsNewView` }
          onClick={ () => toggleCreateModal(true) }
          disabled={ !canCreateView }
          tooltipContent={
            applyAndSaveNewViewTooltip ?? (!canCreateView ? BOND_SCREENER_VIEWS_LIMIT_MESSAGE : undefined)
          }
        >
          Apply and Save as a New View
        </ButtonDropDownItem>
      </ButtonDropDown>
      <BondScreenerCreateViewModal
        isVisible={ isCreateModalOpen }
        existingNames={ existingNames }
        onCreate={ handleCreateView }
        onCancel={ () => toggleCreateModal(false) }
      />
    </>
  );
};

export default BondScreenerApplySaveButton;
